import React, { useState, useRef, useMemo } from 'react'
import { FlexProps, Spinner } from '@chakra-ui/react'
import { IconButton } from '@chakra-ui/react'
import { useBoolean } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import { useOutsideClick } from '@chakra-ui/react'
import { Box, Flex, Text } from '@chakra-ui/react'
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
} from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'

import { scrollbarCSS } from '~components/dashboard/action-item-list'
import { adjustArrayWithItem } from '~utils/helpers'
import SharedHelpText from './shared-help-text'
import SharedSearchInput from '../shared-search-input'

interface Props<T> {
    options: T[]
    filteredOptions?: T[]
    selectedOptions: T[]
    setSelectedOptions: (options: T[]) => void
    searchByFields?: (keyof T)[]
    isSelected?: (option: T) => boolean
    displayTransformer?: (o: T) => string | React.ReactNode
    placeholder?: string

    // Outside in case of query edits
    setSearch?: (input: string) => void

    // For infinite scrolling
    onScrolledToBottom?: () => void

    isLoading?: boolean
    hasMore?: boolean
    divideOptions?: boolean
    selectedStyle?: FlexProps
}

export function FtsSelect<T>({
    options,
    filteredOptions,
    selectedOptions,
    setSelectedOptions,
    searchByFields,
    isSelected,
    displayTransformer = (o: T) => <Text px={1}>{String(o)}</Text>,
    setSearch,
    placeholder,
    onScrolledToBottom,
    isLoading,
    hasMore,
    divideOptions = false,
    selectedStyle,
}: Props<T>) {
    const [input, setInput] = useState<string>('')
    const [isFocused, setIsFocused] = useBoolean()

    const flexRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const lastScrollTriggerRef = useRef<number>(0)

    /** Distance from bottom (px) at which we trigger load more */
    const SCROLL_LOAD_MORE_THRESHOLD = 32

    useOutsideClick({
        ref: flexRef,
        handler: setIsFocused.off,
    })

    function handleNewSelectedOption(item: T) {
        setSelectedOptions(
            adjustArrayWithItem(selectedOptions, item, isSelected),
        )
    }

    const filtered = useMemo(() => {
        if (filteredOptions) {
            return filteredOptions
        }

        return options.filter(option => {
            if (!searchByFields) {
                return !selectedOptions.includes(option)
            }

            return searchByFields.some(field =>
                String(option[field])
                    .toLowerCase()
                    .includes(input.toLowerCase()),
            )
        })
    }, [filteredOptions, options, selectedOptions, input, searchByFields])

    function handleScroll(e: React.UIEvent<HTMLDivElement>) {
        const now = Date.now()
        const el = e.currentTarget
        const distanceFromBottom =
            el.scrollHeight - (el.scrollTop + el.clientHeight)
        const isNearBottom = distanceFromBottom <= SCROLL_LOAD_MORE_THRESHOLD

        if (isNearBottom && onScrolledToBottom && hasMore && !isLoading) {
            if (now - lastScrollTriggerRef.current >= 750) {
                onScrolledToBottom()
                lastScrollTriggerRef.current = now
            }
        }
    }

    return (
        <Box
            w="full"
            ref={flexRef}
            p={1}
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            rounded="md"
        >
            <Popover placement="bottom" isOpen={isFocused} autoFocus={false}>
                <PopoverTrigger>
                    <Flex
                        w="full"
                        minW="250px"
                        flexWrap="wrap"
                        gap={1}
                        onClick={() => {
                            if (!isFocused) {
                                inputRef.current?.focus()
                            }
                        }}
                    >
                        {selectedOptions.map((option, i) => (
                            <Flex
                                align="center"
                                key={`${displayTransformer(option)}-${i}`}
                                bg="gray.50"
                                border="1px solid"
                                borderColor="gray.300"
                                rounded="md"
                                fontSize="xs"
                                userSelect="none"
                                {...selectedStyle}
                            >
                                {displayTransformer(option)}
                                <IconButton
                                    aria-label="Remove option"
                                    icon={<Icon as={FiX} />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={e => {
                                        e.stopPropagation()
                                        handleNewSelectedOption(option)
                                    }}
                                />
                            </Flex>
                        ))}
                        <SharedSearchInput
                            ref={inputRef}
                            variant="ghost"
                            size="sm"
                            value={input}
                            onChange={e => {
                                if (setSearch) {
                                    setSearch(e.target.value)
                                }
                                setInput(e.target.value)
                            }}
                            onFocus={setIsFocused.on}
                            placeholder={placeholder}
                        />
                    </Flex>
                </PopoverTrigger>
                <PopoverContent
                    w={flexRef.current?.clientWidth}
                    onFocus={() => inputRef.current?.focus()}
                >
                    <PopoverBody w="full" p={0}>
                        <Box
                            ref={scrollContainerRef}
                            maxH="296px"
                            overflowY="auto"
                            css={scrollbarCSS}
                            onScroll={handleScroll}
                        >
                        {filtered.map((p, index) => (
                            <Box
                                key={`${displayTransformer(p)}-${index}`}
                                px={2}
                                py={1}
                                _hover={{ bg: 'brand.50' }}
                                w="full"
                                onClick={e => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    // Clear only the local input display. Do not
                                    // call setSearch('') — for server-driven
                                    // selects that would debounce-clear the
                                    // query and race-wipe the results list.
                                    setInput('')
                                    handleNewSelectedOption(p)
                                }}
                                cursor="pointer"
                                tabIndex={0}
                                borderBottom={
                                    divideOptions ? '1px solid' : 'none'
                                }
                                borderColor="gray.200"
                            >
                                <Text fontSize="sm">
                                    {displayTransformer(p)}
                                </Text>
                            </Box>
                        ))}
                        {!filtered.length && !isLoading && (
                            <Box px={2} py={1}>
                                <SharedHelpText fontStyle="italic" mb={0}>
                                    No results found.
                                </SharedHelpText>
                            </Box>
                        )}
                        {isLoading && (
                            <Flex justify="center" py={2}>
                                <Spinner size="sm" color="gray.400" />
                            </Flex>
                        )}
                        {!isLoading &&
                            !hasMore &&
                            !!lastScrollTriggerRef.current &&
                            filtered.length > 0 && (
                                <Box px={2} py={1}>
                                    <SharedHelpText fontStyle="italic" mb={0}>
                                        No more results.
                                    </SharedHelpText>
                                </Box>
                            )}
                        </Box>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        </Box>
    )
}
